// packages/core/src/learning/schools/frugality.ts
// ─────────────────────────────────────────────────────────────────────────
// Authored exhibit for "Frugality". Matches boglehead-indexing.ts gold
// standard.
//
// ID-bound fields (the validator + compiler guard these):
//   - figures:        FigureId[]  — held [] for the post-authoring cross-ref
//                     pass. Worldview names: vicki-robin, cait-flanders,
//                     courtney-carver, jacob-lund-fisker.
//   - applyActions:   ApplyAction union — type-checked at compile time. Only
//                     live/allowed action types emitted.
//   - featuredLessonIds / debateIds — [] until the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const FRUGALITY_EXHIBIT: SchoolExhibitEntry = {
	id: 'frugality',
	tagline: 'Frugality believes spending less should buy you more life.',
	worldviewSummary:
		'Frugality is paying attention to how ordinary spending quietly steals margin, time, and options. This lens protects you from lifestyle creep, impulse spending, and confusing comfort with freedom. It accepts a real tradeoff: saying no more often can create breathing room, but it can also become too restrictive if the habit turns into fear. The blind spot is that frugality can overfocus on cutting costs when income, housing, health, family support, and location also shape the math. At its best, frugality is not about misery. It is about keeping more of your life from being rented out to things you barely care about.',
	coreBeliefs: [
		'Frugality believes every dollar has a life cost attached to it.',
		'Frugality believes spending less can create freedom faster than people expect.',
		'Frugality believes small leaks matter because repeated behavior becomes a lifestyle.',
		'Frugality believes a good life does not require every upgrade you can afford.',
		'Frugality believes restraint should protect joy, not erase it.',
	],
	warnsAgainst: [
		'Letting lifestyle creep absorb every raise.',
		'Buying convenience so often that your future self loses breathing room.',
		'Confusing cheapness with wisdom.',
		'Cutting costs while ignoring income, health, relationships, and quality of life.',
	],
	helps: 'This lens helps people who want more margin, less waste, and stronger control over daily spending. It may not fit when cutting becomes fear, status denial, or a way to avoid earning power and bigger structural decisions.',
	figures: [],
	canon: [
		{
			title: 'Your Money or Your Life',
			author: 'Vicki Robin',
			oneLineIdea:
				'Spending should be weighed against the life energy it costs.',
		},
		{
			title: 'The Year of Less',
			author: 'Cait Flanders',
			oneLineIdea:
				'Buying less can reveal which habits are emotional escape, not real need.',
		},
		{
			title: 'Soulful Simplicity',
			author: 'Courtney Carver',
			oneLineIdea:
				'Owning and wanting less can create more room for attention and peace.',
		},
		{
			title: 'Early Retirement Extreme',
			author: 'Jacob Lund Fisker',
			oneLineIdea:
				'A lower cost life can radically change how much money freedom requires.',
		},
		{
			title: 'Conscious spending',
			oneLineIdea:
				'Frugality works best when it cuts what matters less and protects what matters more.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-frugality',
	debateIds: [],
	applyActions: [
		{
			label: 'See what your spending gap does to your freedom path',
			action: {
				type: 'analyzeSavingsRate',
				label: 'Check your savings rate',
			},
		},
		{
			label: 'Test what lower monthly spending changes',
			action: {
				type: 'openSimulator',
				label: 'Simulate lower spending',
				scenarioType: 'expense-slash',
			},
		},
		{
			label: 'Name what spending actually gives you back',
			action: {
				type: 'noToolAction',
				label: 'Audit one spending habit',
				prompt: 'Pick one recurring expense and write what it gives you in comfort, time, status, or stress relief.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Frugality is not about being cheap. It is about buying your life back.',
			kind: 'hook',
		},
		{
			line: 'The hidden cost of small leaks is not dollars. It is lost breathing room.',
			kind: 'hook',
		},
		{
			line: 'What purchase did you stop making that gave you more peace than expected?',
			kind: 'interview-question',
		},
		{
			line: 'Ask someone what they cut from their life and what they gained back.',
			kind: 'creator-prompt',
		},
		{
			line: 'Nisk helps users test whether spending less creates real options, not just guilt.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show your audience the difference between frugal, cheap, and actually free.',
			kind: 'creator-prompt',
		},
		{
			line: 'Where does frugality help, and where does it become too small minded?',
			kind: 'interview-question',
		},
		{
			line: 'A raise does not change your life if your lifestyle eats it first.',
			kind: 'hook',
		},
	],
	revision: 1,
};
